import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import { useAnonymousPui } from './use-anonymous-pui';
import { insuranceCarrierDefs, logError } from '@shared';

export function useSendAnonymousPui() {
  const client = useSelector(selectClient);
  const { mutate: sendPuiRequest, isPending, isSuccess, puiSentData } = useAnonymousPui();
  const [showDialog, setShowDialog] = useState(false);

  // Extract policy information from multipleFundingSources
  const availablePolicies = useMemo<PolicyInfo[]>(() => {
    if (!client?.multipleFundingSources?.ltcPolicy) {
      return [];
    }

    const policiesMap = new Map<string, PolicyInfo>();

    Object.entries(client.multipleFundingSources.ltcPolicy).forEach(([_, policy]) => {
      let productName: string | undefined;
      let isJoint = false;

      // sourceQuote can be an object or a JSON string
      if (policy.sourceQuote) {
        if (typeof policy.sourceQuote === 'string') {
          try {
            const parsed = JSON.parse(policy.sourceQuote);
            productName = parsed.productName;
            const policySourceQuote = parsed as PolicyQuote;
            isJoint =
              policySourceQuote.policyType === 'Joint Life' ||
              policySourceQuote.policyType === 'Couples Discount';
          } catch (error) {
            // Failed to parse sourceQuote JSON
            logError('use-send-anonymous-pui', 'Failed to parse sourceQuote JSON', {
              sourceQuote: policy.sourceQuote,
              error: (error as Error).message,
            });
          }
        } else if (typeof policy.sourceQuote === 'object') {
          productName = policy.sourceQuote.productName;
          const policySourceQuote = policy.sourceQuote as PolicyQuote;
          isJoint =
            policySourceQuote.policyType === 'Joint Life' ||
            policySourceQuote.policyType === 'Couples Discount';
        }
      }

      if (productName && policy.policyType) {
        // Find carrier that supports this policy type
        const carrier = Object.values(insuranceCarrierDefs).find(carrierDef =>
          carrierDef.policyTypes.includes(policy.policyType!),
        );

        const supportsPUI = carrier?.supportsPUI === true;
        const uniqueKey = `${productName}-${policy.policyType}-${isJoint}`;

        if (!policiesMap.has(uniqueKey)) {
          policiesMap.set(uniqueKey, {
            productName,
            fundingPolicyType: policy.policyType,
            isJoint,
            supportsPUI,
            uniqueKey,
          });
        }
      }
    });

    return Array.from(policiesMap.values());
  }, [client?.multipleFundingSources?.ltcPolicy]);

  const handleOpenDialog = useCallback(() => {
    setShowDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setShowDialog(false);
  }, []);

  const handleSendPui = useCallback(
    (options: SendAnonymousPuiOptions) => {
      sendPuiRequest(options);
    },
    [sendPuiRequest],
  );

  return {
    showDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleSendPui,
    isPending,
    isSuccess,
    puiSentData,
    availablePolicies,
  };
}
